/**
 * 管理AppのメニューをLoadする。
 */
import { genLinkWithModelDef, genLinkWithModelNames } from './appMenuDefinitionParser'
import { generateAppLink } from '../../plugins/AppDefinitions/front/AppDefinitionLoaderService'
import { generateLinkComponentDef } from './generateSidebarLinksToOtherApps'
import type { LayoutMenuItem } from '../Layouts/menuTypes' // ★ Import new type

export const generateAdminAppMenu = async (): Promise<LayoutMenuItem[]> => {
  const appDefinitions = Object.values(
    $core.$appDefinitionLoader.allAppDefinitionsByKey || {},
  )
  const appLinks: LayoutMenuItem[] = appDefinitions.map((appDef) => ({
    id: `app-${appDef.key}`,
    type: 'component',
    label: appDef.name, // Add label for clarity
    componentDefinition: generateLinkComponentDef(
      generateAppLink(appDef.key),
      appDef.name,
      'nav-link', // Base class
      'external-link-alt', // Icon
    ),
  }))

  const menu: LayoutMenuItem[] = [
    { id: 'heading-apps', type: 'heading', label: 'アプリケーション', isHeader: true },
    {
      id: 'link-appDefs',
      type: 'link',
      label: 'アプリケーション定義一覧',
      to: '/m/appDefinitions',
      icon: 'cubes',
    }, // Add icon
    ...appLinks,
    { id: 'heading-models', type: 'heading', label: 'データモデル定義', isHeader: true },
    {
      id: 'link-modelDefs',
      type: 'link',
      label: 'モデル定義',
      to: '/m/modelDefinitions',
      icon: 'database',
    },
    {
      id: 'nested-models',
      type: 'nested',
      label: 'モデル一覧',
      icon: 'list-ul',
      children: genLinkWithModelNames(
        Object.keys($core.$models || {}).filter(
          (modelName) => $core.$models[modelName].modelType !== 'admin',
        ),
      ),
    },
    {
      id: 'link-vmodelDefs',
      type: 'link',
      label: 'Virtualモデル定義',
      to: '/m/virtualModelDefinitions',
      icon: 'puzzle-piece',
    },
    {
      id: 'nested-vmodels',
      type: 'nested',
      label: 'Virtualモデル一覧',
      icon: 'list-ul',
      children: Object.keys($core.$virtualModels || {})
        .filter((modelName) => $core.$virtualModels[modelName].modelType !== 'admin')
        .map((modelName) => genLinkWithModelDef($core.$virtualModels[modelName]))
        .filter((item) => item !== null) as LayoutMenuItem[], // Filter out nulls
    },
    { id: 'heading-admin', type: 'heading', label: '管理', isHeader: true },
    {
      id: 'nested-data',
      type: 'nested',
      label: 'データ・マスタ管理',
      icon: 'tasks',
      children: [
        ...genLinkWithModelNames(['dataImportSettings']),
        {
          id: 'link-import',
          type: 'link',
          label: 'データインポート実行',
          to: '/importDataFromExternal',
          icon: 'file-import',
        },
        ...genLinkWithModelNames(['dataExportSettings']),
        {
          id: 'link-export',
          type: 'link',
          label: 'データエクスポート実行',
          to: '/exportData',
          icon: 'file-export',
        },
        ...genLinkWithModelNames([
          'selectOptionsMaster',
          'textTemplates',
          'files',
          'directus_revisions',
          'appDocumentPages',
        ]),
        {
          id: 'link-sql',
          type: 'link',
          label: 'SQLマネージャ',
          to: '/db-direct-query-executor',
          icon: 'terminal',
        },
      ],
    },
    {
      id: 'nested-users',
      type: 'nested',
      label: 'ユーザ・権限管理',
      icon: 'users-cog',
      children: [
        genLinkWithModelDef($core.$virtualModels?.coreAppUsers),
        genLinkWithModelDef($core.$models?.user_roles),
        { id: 'link-org', type: 'link', label: '組織', to: '/editOrg', icon: 'sitemap' },
        ...genLinkWithModelNames(['positions']),
      ].filter((item) => item !== null) as LayoutMenuItem[], // Filter out nulls
    },
    {
      id: 'nested-components',
      type: 'nested',
      label: 'コンポーネント管理',
      icon: 'puzzle-piece',
      children: [
        {
          id: 'link-composable',
          type: 'link',
          label: 'コンポーザブルコンポーネント',
          to: '/m/core_composable_component_definitions',
        },
        // { id: 'link-chartbuilder', type: 'link', label: 'チャートビルダー', to: '/m/customComponentDefinitions?virtualModel=chartDefinitions' },
      ],
    },
    {
      id: 'nested-api',
      type: 'nested',
      label: 'API・データソース設定',
      icon: 'server',
      children: [
        { id: 'link-apiList', type: 'link', label: 'API一覧', to: '/api/list' },
        ...genLinkWithModelNames([
          'core_another_data_source_connection_definitions',
          'core_another_data_source_dialects',
        ]),
        // --- Component Links for Actions ---
        {
          id: 'link-glitchTipIssues',
          type: 'link',
          label: 'GlitchTip Issues',
          to: '/glitchtip-issues',
        },
        {
          type: 'link',
          label: '管理操作',
          to: '/admin-operations',
        },
      ],
    },
    {
      id: 'nested-customize',
      type: 'nested',
      label: 'カスタマイズ',
      icon: 'code',
      children: genLinkWithModelNames(['serverSideAppHooks', 'frontSideAppHooks']),
    },
  ]

  // Allow extending the admin menu via AppHook
  if ($core.$appHook.hasHook('adminAppMenu')) {
    // Ensure the hook returns the correct type or adapt it
    const modifiedMenu = await $core.$appHook.emit('adminAppMenu', menu)
    // TODO: Validate or transform modifiedMenu to LayoutMenuItem[] if necessary
    return modifiedMenu as LayoutMenuItem[]
  }
  return menu
}
